import React, { useState } from 'react';
import styled from 'styled-components';
import { authenticateInvestorAdmin } from '../../utils/supabase';

const Page = styled.div`
  min-height: 100%;
  padding: 32px 20px 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
`;

const Subtitle = styled.p`
  color: #a1a1aa;
  font-size: 14px;
`;

const Card = styled.div`
  border: 1px solid #333333;
  border-radius: 16px;
  padding: 20px;
  background: #000000;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #a1a1aa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  background: #000000;
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 12px 14px;
  color: #ffffff;
  font-size: 14px;
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 13px;
`;

const Note = styled.div`
  font-size: 12px;
  color: #71717a;
  line-height: 1.4;
`;

interface InvestorAdminLoginScreenProps {
  onSuccess: () => void;
}

const InvestorAdminLoginScreen: React.FC<InvestorAdminLoginScreenProps> = ({ onSuccess }) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!name.trim() || !password.trim()) {
      setError('Please enter your admin name and password.');
      return;
    }

    setLoading(true);
    const result = await authenticateInvestorAdmin(name.trim(), password);
    setLoading(false);

    if (!result.success || !result.admin) {
      setError(result.error || 'Invalid credentials.');
      return;
    }

    localStorage.setItem('investorAdminName', result.admin.admin_name);
    localStorage.setItem('investorAdminId', result.admin.id);
    onSuccess();
  };

  return (
    <Page>
      <div>
        <Title>Admin Access</Title>
        <Subtitle>Sign in to manage investor portfolios.</Subtitle>
      </div>
      <Card>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="admin-name">Admin Name</Label>
            <Input
              id="admin-name"
              type="text"
              placeholder="Admin name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="username"
            />
          </Field>
          <Field>
            <Label htmlFor="admin-password">Password</Label>
            <Input
              id="admin-password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          {error && <ErrorText>{error}</ErrorText>}
          <Button type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </Card>
      <Note>
        Admin access is restricted to approved operators.
      </Note>
    </Page>
  );
};

export default InvestorAdminLoginScreen;
